import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

function DataTable({ columns, rows, onEdit, onDelete, testPrefix }) {
  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
      <div className="thin-scroll overflow-x-auto">
      <table className="w-full min-w-[560px] text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className="px-4 py-3 font-semibold">
                {c.label}
              </th>
            ))}
            <th className="px-4 py-3 text-right font-semibold">Aksi</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} className="px-4 py-10 text-center text-slate-400">
                Belum ada data.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.id} data-testid={`${testPrefix}-row-${row.id}`} className="hover:bg-slate-50">
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3 text-slate-700">
                    {c.render ? c.render(row) : row[c.key] || "-"}
                  </td>
                ))}
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="icon" onClick={() => onEdit(row)} data-testid={`${testPrefix}-edit-${row.id}`}>
                    <Pencil className="h-4 w-4 text-slate-500" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => onDelete(row)} data-testid={`${testPrefix}-delete-${row.id}`}>
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>

    </div>
  );
}

export default function Admin() {
  const [divisions, setDivisions] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);

  const loadAll = () => {
    api.get("/divisions").then(({ data }) => setDivisions(data));
    api.get("/workshops").then(({ data }) => setWorkshops(data));
    api.get("/vehicles").then(({ data }) => setVehicles(data));
    api.get("/users").then(({ data }) => setUsers(data));
  };

  useEffect(loadAll, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900 md:text-5xl">
          Master Data
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Kelola pengguna, divisi, bengkel, dan data kendaraan.
        </p>
      </div>

      <Tabs defaultValue="users">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="users" data-testid="tab-users">Pengguna</TabsTrigger>
          <TabsTrigger value="divisions" data-testid="tab-divisions">Divisi</TabsTrigger>
          <TabsTrigger value="workshops" data-testid="tab-workshops">Bengkel</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="tab-vehicles">Kendaraan</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <UsersTab users={users} divisions={divisions} workshops={workshops} reload={loadAll} />
        </TabsContent>
        <TabsContent value="divisions" className="mt-4">
          <DivisionsTab divisions={divisions} reload={loadAll} />
        </TabsContent>
        <TabsContent value="workshops" className="mt-4">
          <WorkshopsTab workshops={workshops} reload={loadAll} />
        </TabsContent>
        <TabsContent value="vehicles" className="mt-4">
          <VehiclesTab vehicles={vehicles} divisions={divisions} reload={loadAll} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------- Users ----------------
function UsersTab({ users, divisions, workshops, reload }) {
  const empty = { name: "", email: "", password: "", role: "pelapor", division_id: "", workshop_id: "", phone: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (u) => {
    setForm({ name: u.name, email: u.email, password: "", role: u.role, division_id: u.division_id || "", workshop_id: u.workshop_id || "", phone: u.phone || "" });
    setEditing(u); setOpen(true);
  };

  const save = async () => {
    const payload = { ...form };
    if (!payload.division_id) payload.division_id = null;
    if (!payload.workshop_id) payload.workshop_id = null;
    try {
      if (editing) {
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users", payload);
      }
      toast.success("Pengguna disimpan");
      setOpen(false); reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Hapus pengguna ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); reload(); toast.success("Dihapus"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button data-testid="add-user-btn" className="bg-[var(--sipkd-primary)] hover:bg-[var(--sipkd-primary-hover)]" onClick={openNew}>
          <Plus className="mr-1 h-4 w-4" /> Tambah Pengguna
        </Button>
      </div>
      <DataTable
        testPrefix="user"
        columns={[
          { key: "name", label: "Nama" },
          { key: "email", label: "Email" },
          { key: "role", label: "Peran", render: (r) => ROLE_LABELS[r.role] || r.role },
          { key: "division_name", label: "Divisi" },
          { key: "workshop_name", label: "Bengkel" },
        ]}
        rows={users}
        onEdit={openEdit}
        onDelete={del}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Pengguna" : "Tambah Pengguna"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Nama</Label><Input data-testid="user-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} disabled={!!editing} /></div>
            <div className="space-y-1.5"><Label>{editing ? "Sandi Baru (opsional)" : "Kata Sandi"}</Label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>No. Telepon</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label>Peran</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(form.role === "pelapor" || form.role === "approver") && (
              <div className="space-y-1.5">
                <Label>Divisi</Label>
                <Select value={form.division_id} onValueChange={(v) => set("division_id", v)}>
                  <SelectTrigger data-testid="user-division"><SelectValue placeholder="Pilih divisi" /></SelectTrigger>
                  <SelectContent>{divisions.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.role === "technician" && (
              <div className="space-y-1.5">
                <Label>Bengkel</Label>
                <Select value={form.workshop_id} onValueChange={(v) => set("workshop_id", v)}>
                  <SelectTrigger data-testid="user-workshop"><SelectValue placeholder="Pilih bengkel" /></SelectTrigger>
                  <SelectContent>{workshops.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="save-user-btn" className="bg-[var(--sipkd-primary)] hover:bg-[var(--sipkd-primary-hover)]" onClick={save}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Generic simple CRUD ----------------
function SimpleTab({ items, columns, fields, testPrefix, endpoint, reload, title }) {
  const empty = Object.fromEntries(fields.map((f) => [f.key, ""]));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (row) => {
    setForm(Object.fromEntries(fields.map((f) => [f.key, row[f.key] || ""])));
    setEditing(row); setOpen(true);
  };
  const save = async () => {
    const payload = { ...form };
    fields.forEach((f) => { if (f.optional && payload[f.key] === "") payload[f.key] = null; });
    try {
      if (editing) await api.put(`/${endpoint}/${editing.id}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success("Tersimpan"); setOpen(false); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (row) => {
    if (!window.confirm("Hapus data ini?")) return;
    await api.delete(`/${endpoint}/${row.id}`); reload(); toast.success("Dihapus");
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button data-testid={`add-${testPrefix}-btn`} className="bg-[var(--sipkd-primary)] hover:bg-[var(--sipkd-primary-hover)]" onClick={openNew}>
          <Plus className="mr-1 h-4 w-4" /> Tambah {title}
        </Button>
      </div>
      <DataTable testPrefix={testPrefix} columns={columns} rows={items} onEdit={openEdit} onDelete={del} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? `Edit ${title}` : `Tambah ${title}`}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {fields.map((f) =>
              f.type === "select" ? (
                <div key={f.key} className="space-y-1.5">
                  <Label>{f.label}</Label>
                  <Select value={form[f.key] || ""} onValueChange={(v) => set(f.key, v)}>
                    <SelectTrigger data-testid={`${testPrefix}-${f.key}`}><SelectValue placeholder={`Pilih ${f.label}`} /></SelectTrigger>
                    <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : (
                <div key={f.key} className="space-y-1.5">
                  <Label>{f.label}</Label>
                  <Input data-testid={`${testPrefix}-${f.key}`} value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
                </div>
              )
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid={`save-${testPrefix}-btn`} className="bg-[var(--sipkd-primary)] hover:bg-[var(--sipkd-primary-hover)]" onClick={save}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DivisionsTab({ divisions, reload }) {
  return (
    <SimpleTab
      title="Divisi" testPrefix="division" endpoint="divisions" reload={reload} items={divisions}
      columns={[{ key: "name", label: "Nama Divisi" }, { key: "description", label: "Keterangan" }]}
      fields={[{ key: "name", label: "Nama Divisi" }, { key: "description", label: "Keterangan", optional: true }]}
    />
  );
}
function WorkshopsTab({ workshops, reload }) {
  return (
    <SimpleTab
      title="Bengkel" testPrefix="workshop" endpoint="workshops" reload={reload} items={workshops}
      columns={[{ key: "name", label: "Nama Bengkel" }, { key: "address", label: "Alamat" }, { key: "phone", label: "Telepon" }]}
      fields={[{ key: "name", label: "Nama Bengkel" }, { key: "address", label: "Alamat", optional: true }, { key: "phone", label: "Telepon", optional: true }]}
    />
  );
}
function VehiclesTab({ vehicles, divisions, reload }) {
  return (
    <SimpleTab
      title="Kendaraan" testPrefix="vehicle" endpoint="vehicles" reload={reload} items={vehicles}
      columns={[
        { key: "plate_number", label: "No. Polisi" },
        { key: "brand", label: "Merek" },
        { key: "type", label: "Tipe" },
        { key: "year", label: "Tahun" },
      ]}
      fields={[
        { key: "plate_number", label: "Nomor Polisi" },
        { key: "brand", label: "Merek", optional: true },
        { key: "type", label: "Tipe", optional: true },
        { key: "year", label: "Tahun", optional: true },
        { key: "division_id", label: "Divisi", type: "select", optional: true, options: divisions.map((d) => ({ value: d.id, label: d.name })) },
      ]}
    />
  );
}
